/* eslint-disable @typescript-eslint/no-explicit-any */
import { getFormProps, getTextareaProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useFetcher } from 'react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { StatusButton } from '../button/status-button'
import { AuthContainerHeader } from '../container/auth-container'
import LoginModal from '../login-modal/login-modal'
import { reportReasons } from './event-report.helper'
import { Button } from '~/components/button/button'
import { ErrorList } from '~/components/error-list/error-list'
import { TextareaField } from '~/components/forms/forms'
import { Icon } from '~/components/icon/icon'
import Modal from '~/components/modal/modal'
import RadioButton from '~/components/radio-button/radio-button'
import Text from '~/components/text/text'

import { useIsPending } from '~/utils/misc'
import { useOptionalUser } from '~/utils/user'
import type { EventBase } from '~/types/event'

export const reportEventSchema = z.object({
	eventId: z.string(),
	reason: z.string({
		required_error: 'form_error_reason_required',
	}),
	description: z.string({
		required_error: 'form_error_reason_required_description',
	}),
	formType: z.string().optional(),
})

export default function EventReportBlock({ event }: { event: EventBase }) {
	const fetcher = useFetcher()
	const user = useOptionalUser()
	const isPending = useIsPending()
	const { t } = useTranslation()
	const [reportReason, setReportReason] = useState<string>('wrong-info')
	const [showReportModal, setShowReportModal] = useState(false)
	const [loginModalOpen, setLoginModalOpen] = useState(false)
	const toggleReportModal = () => setShowReportModal(prev => !prev)

	const [form, fields] = useForm({
		id: 'report-event',
		constraint: getZodConstraint(reportEventSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: reportEventSchema })
		},
		shouldRevalidate: 'onInput',
	})

	const handleReportChange = (value: string) => {
		setReportReason(value)
	}

	if (user && user.id === event.owner.id) {
		return undefined
	}

	return (
		<div className="relative">
			{showReportModal && (
				<Modal
					onClose={toggleReportModal}
					title={
						fetcher?.data?.result.status !== 'success'
							? t('report_why_report_event')
							: undefined
					}
				>
					{fetcher?.data?.result.status !== 'success' ? (
						<fetcher.Form method="POST" {...getFormProps(form)}>
							<input name="reason" value={reportReason} hidden readOnly />
							<input name="formType" value="report" hidden readOnly />
							<input name="eventId" value={event.id} hidden readOnly />
							<div className="mb-6 mt-2 flex flex-col gap-y-2">
								{reportReasons.map(x => (
									<RadioButton
										key={x.value}
										checked={reportReason === x.value}
										value={x.value}
										onClick={handleReportChange}
										label={t(x.label as any)}
									/>
								))}
								<ErrorList errors={fields.reason.errors} />
							</div>
							<TextareaField
								labelProps={{
									htmlFor: fields.description.id,
									children: t('form_label_description'),
								}}
								textareaProps={{ ...getTextareaProps(fields.description) }}
								errors={fields.description.errors}
							/>
							<StatusButton
								type="submit"
								status={isPending ? 'pending' : 'idle'}
								disabled={isPending}
							>
								{t('events_reported_events')}
							</StatusButton>
						</fetcher.Form>
					) : (
						<>
							<AuthContainerHeader
								title={t('report_thank_you')}
								text={t('report_recieved')}
								icon="check-badge"
							/>
						</>
					)}
				</Modal>
			)}

			<div className="flex justify-center">
				{event.reportedByUser ? (
					<Text className="text-center font-semibold !text-foreground-danger">
						<Icon name="warning-triangle" size="lg" />
						<br />
						{t('report_already_reported')}
					</Text>
				) : user ? (
					<Button type="button" variant="link" onClick={toggleReportModal}>
						{t('report_report_this_event')}
					</Button>
				) : (
					<>
						<Button
							onClick={() => setLoginModalOpen(!loginModalOpen)}
							size="md"
							variant="link"
						>
							{t('report_report_this_event')}
						</Button>
						{loginModalOpen && (
							<LoginModal
								initialPage={0}
								toggleLoginModal={() => setLoginModalOpen(!loginModalOpen)}
								text={t('report_you_have_to_login')}
							/>
						)}
					</>
				)}
			</div>
		</div>
	)
}
